/**
 * AI Solve (photograph a question) and Chats.
 *
 * Both are metered against the daily AI budget. When it is spent the API answers
 * with a 429 whose `detail` is written for a student to read — show it as written
 * rather than replacing it with a generic error.
 */

import type { ApiClient } from '../core/client'
import type { ChatStreamFrame } from '../core/sse'
import { streamFrames } from '../core/sse'
import type { UUID } from '../types/common'
import type {
  Chat,
  ChatCreate,
  ChatStreamCreate,
  PhotoFollowUp,
  PhotoHistoryItem,
  PhotoSavedSolution,
  PhotoSolveRequest,
} from '../types/ai'

export class AiSolveResource {
  constructor(private readonly client: ApiClient) {}

  history(signal?: AbortSignal): Promise<PhotoHistoryItem[]> {
    return this.client.get<PhotoHistoryItem[]>('/ai-solve/history', undefined, signal)
  }

  /** Solves a question and records it. `image_data` is base64 with no data-URI
   *  prefix — `imageToBase64` in `@modulus/ui` strips it. */
  solve(body: PhotoSolveRequest, signal?: AbortSignal): Promise<PhotoHistoryItem> {
    return this.client.post<PhotoHistoryItem>('/ai-solve/history', body, signal ? { signal } : {})
  }

  /** Re-runs an existing item against a new (usually cropped) image. */
  rescan(itemId: UUID, body: { image_data: string; question?: string }): Promise<PhotoHistoryItem> {
    return this.client.put<PhotoHistoryItem>(`/ai-solve/history/${itemId}/rescan`, body)
  }

  setSaved(itemId: UUID, isSaved: boolean): Promise<unknown> {
    return this.client.put<unknown>(`/ai-solve/history/${itemId}/save-status`, { is_saved: isSaved })
  }

  followUps(itemId: UUID, signal?: AbortSignal): Promise<PhotoFollowUp[]> {
    return this.client.get<PhotoFollowUp[]>(`/ai-solve/history/${itemId}/followups`, undefined, signal)
  }

  askFollowUp(itemId: UUID, message: string): Promise<PhotoFollowUp> {
    return this.client.post<PhotoFollowUp>(`/ai-solve/history/${itemId}/followup`, { message })
  }

  saved(signal?: AbortSignal): Promise<PhotoSavedSolution[]> {
    return this.client.get<PhotoSavedSolution[]>('/ai-solve/saved', undefined, signal)
  }

  /** Upsert, for a solution that does not exist in history yet. */
  saveSolution(item: PhotoSavedSolution): Promise<unknown> {
    return this.client.post<unknown>('/ai-solve/saved', item)
  }

  deleteHistoryItem(itemId: UUID): Promise<void> {
    return this.client.delete<void>(`/ai-solve/history/${itemId}`)
  }

  /** Clears everything. The caller confirms — this does not. */
  clearHistory(): Promise<void> {
    return this.client.delete<void>('/ai-solve/history/all')
  }
}

export class ChatsResource {
  constructor(private readonly client: ApiClient) {}

  list(signal?: AbortSignal): Promise<Chat[]> {
    return this.client.get<Chat[]>('/chats/', undefined, signal)
  }

  get(chatId: UUID, signal?: AbortSignal): Promise<Chat> {
    return this.client.get<Chat>(`/chats/${chatId}`, undefined, signal)
  }

  create(body: ChatCreate): Promise<Chat> {
    return this.client.post<Chat>('/chats/', body)
  }

  update(chatId: UUID, body: Partial<ChatCreate>): Promise<Chat> {
    return this.client.put<Chat>(`/chats/${chatId}`, body)
  }

  rename(chatId: UUID, title: string): Promise<Chat> {
    return this.client.put<Chat>(`/chats/${chatId}/title`, { title })
  }

  delete(chatId: UUID): Promise<void> {
    return this.client.delete<void>(`/chats/${chatId}`)
  }

  /** Non-streaming send. Use `sendMessageStream` in any UI the student watches. */
  sendMessage(chatId: UUID, text: string, imageData?: string): Promise<unknown> {
    return this.client.post<unknown>(`/chats/${chatId}/messages`, {
      text,
      ...(imageData ? { image_data: imageData } : {}),
    })
  }

  /**
   * Creates a thread and answers its first message in one request.
   *
   * The thread is committed before the first frame, so a `get` works the moment the
   * client sees anything. If the answer then fails, the chat, the student's question
   * and an error bubble all survive — they never lose what they wrote, which matters
   * most when it was a photo.
   */
  createAndStream(
    body: ChatStreamCreate,
    signal?: AbortSignal,
  ): AsyncGenerator<ChatStreamFrame, void, undefined> {
    return streamFrames<ChatStreamFrame>(this.client, '/chats/stream', body, signal ? { signal } : {})
  }

  /** `text` on each frame is a delta to append, not the whole answer so far. */
  sendMessageStream(
    chatId: UUID,
    text: string,
    options: { imageData?: string; signal?: AbortSignal } = {},
  ): AsyncGenerator<ChatStreamFrame, void, undefined> {
    return streamFrames<ChatStreamFrame>(
      this.client,
      `/chats/${chatId}/messages/stream`,
      { text, ...(options.imageData ? { image_data: options.imageData } : {}) },
      options.signal ? { signal: options.signal } : {},
    )
  }
}
